import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { Button, Field, Input, Label } from "@headlessui/react";
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { handleEnterSpaceKey } from "../../app/keyboardUtils";
import { isHigherThanDistrictAdmin } from "../../app/userSlice";
import { useAppSelector } from "../../app/hooks";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import { ClassLinkOrCleverIcon } from "../../components/ClassLinkOrCleverIcon";
import ModalFrame from "../../components/ModalFrame";
import RequiredField from "../../components/RequiredField";
import { SimpleOption } from "../../components/input/SimpleListbox";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import Select from "../../components/input/Select";
import {
	_defaultCountry,
	useGetCountriesQuery,
	useLazyGetZonesQuery,
} from "../countries/countriesApiSlice";
import { useGetDistrictsQuery } from "../districts/districtsApiSlice";
import SchoolOutsideSiteSettingsForm from "./schoolSettings/SchoolOutsideSiteSettingsForm";
import {
	SchoolEditModel,
	SchoolSpaViewModel,
	useAddSchoolMutation,
	useEditSchoolMutation,
	useGenerateReferralCodeMutation,
} from "./schoolsApiSlice";

interface Props {
	schoolInfo: SchoolSpaViewModel;
	addingNew: boolean;
	setIsDialogOpen?: (isOpen: boolean) => void;
	setSchoolInfo?: (schoolInfo: SchoolSpaViewModel) => void;
}

export default function SchoolInfoSection(props: Props) {
	const { role } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const [firstLoad, setFirstLoad] = useState(true);
	const [saveError, setSaveError] = useState("");
	const [disableSave, setDisableSave] = useState(false);

	const [schoolName, setSchoolName] = useState(props.schoolInfo.name ?? "");
	const [selectedDistrictId, setSelectedDistrictId] = useState(props.schoolInfo.district?.id);
	const [outsideSiteId, setOutsideSiteId] = useState(
		props.schoolInfo.showCleverID
			? (props.schoolInfo.cleverID ?? "")
			: (props.schoolInfo.classLinkID ?? "")
	);
	const [externalId, setExternalId] = useState(props.schoolInfo.externalID ?? "");
	const [referralCode, setReferralCode] = useState(props.schoolInfo.referralCode ?? "");

	const [streetAddress, setStreetAddress] = useState(props.schoolInfo.address ?? "");
	const [streetAddress2, setStreetAddress2] = useState(props.schoolInfo.address2 ?? "");
	const [city, setCity] = useState(props.schoolInfo.city ?? "");
	const [selectedStateId, setSelectedStateId] = useState(props.schoolInfo.zoneId);
	const [selectedCountryId, setSelectedCountryId] = useState(props.schoolInfo.countryId);
	const [zip, setZip] = useState(props.schoolInfo.zipCode ?? "");
	const [contactName, setContactName] = useState(props.schoolInfo.contactName ?? "");
	const [contactEmail, setContactEmail] = useState(props.schoolInfo.contactEmail ?? "");
	const [contactPhone, setContactPhone] = useState(props.schoolInfo.contactPhone ?? "");

	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [stateOptions, setStateOptions] = useState<SimpleOption[]>([]);
	const [countryOptions, setCountryOptions] = useState<SimpleOption[]>([]);

	const [showAddressSection, setShowAddressSection] = useState(props.addingNew ? true : false);
	const [isOutsideSettingsFormOpen, setIsOutsideSettingsFormOpen] = useState(false);
	const showOutsideSite =
		!props.addingNew && (props.schoolInfo.showCleverID || props.schoolInfo.showClassLinkID);

	const getDistrictsResult = useGetDistrictsQuery();
	const districts = getDistrictsResult.data;

	const getCountriesResult = useGetCountriesQuery();
	const countries = getCountriesResult.data;

	const [callGetZonesForCountry, callGetZonesForCountryResult] = useLazyGetZonesQuery();

	const [addSchool] = useAddSchoolMutation();
	const [editSchool] = useEditSchoolMutation();
	const [generateReferralCode] = useGenerateReferralCodeMutation();

	const navigate = useNavigate();

	function toggleShowAddressSection() {
		setShowAddressSection(!showAddressSection);
	}

	useEffect(() => {
		if (districts) {
			const options = districts.map((d) => ({ value: d.id, label: d.name }) as SimpleOption);
			if (isHigherThanDistrictAdmin(role!)) {
				options.unshift({ value: "", label: "None" } as SimpleOption);
			}

			setDistrictOptions(options);
		}
	}, [districts]);

	useEffect(() => {
		if (countries) {
			const options = countries.map((c) => ({ value: c.id, label: c.name }) as SimpleOption);

			setCountryOptions(options);
			if (!selectedCountryId) {
				setSelectedCountryId(countries.find((c) => c.name == _defaultCountry)?.id ?? 0);
			}
		}
	}, [countries]);

	useEffect(() => {
		if (selectedCountryId) {
			callGetZonesForCountry(selectedCountryId)
				.unwrap()
				.then((zones) => {
					if (zones) {
						const options = zones.map(
							(z) => ({ value: z.id, label: z.name }) as SimpleOption
						);

						setStateOptions(options);

						const stateId =
							options.find((z) => z.label == (config?.defaultState ?? ""))?.value ??
							options[0].value;

						if (!firstLoad || !selectedStateId) setSelectedStateId(Number(stateId));
					}
				});

			setFirstLoad(false);
		}
	}, [selectedCountryId]);

	useEffect(() => {
		if (!schoolName || !streetAddress || !city || !contactName || !contactPhone) {
			setDisableSave(true);
		} else {
			setDisableSave(false);
		}
	}, [schoolName, streetAddress, city, contactName, contactPhone]);

	function updateModel(): SchoolEditModel {
		const model: SchoolSpaViewModel = {
			...props.schoolInfo,
			name: schoolName,
			district: districts?.find((d) => d.id == selectedDistrictId),
			cleverID: props.schoolInfo.cleverID ?? "",
			classLinkID: props.schoolInfo.classLinkID ?? "",
			externalID: externalId,
			referralCode: referralCode,
			address: streetAddress,
			address2: streetAddress2,
			city: city,
			zoneId: selectedStateId,
			countryId: selectedCountryId,
			zipCode: zip,
			contactName: contactName,
			contactEmail: contactEmail,
			contactPhone: contactPhone,
		};
		return { id: props.schoolInfo.id, model: model };
	}

	function handleCancel() {
		props.addingNew && props.setIsDialogOpen
			? props.setIsDialogOpen(false)
			: navigate(`/schools`);
	}

	function handleSave(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
		e.preventDefault();
		const model = updateModel();

		if (model) {
			if (props.addingNew) {
				addSchool(model.model)
					.unwrap()
					.then((id) => {
						if (props.setIsDialogOpen) props.setIsDialogOpen(false);
						navigate(`/schools/edit?schoolId=${id}`);
					})
					.catch((error) => {
						setSaveError(error.data);
					});
			} else {
				editSchool(model)
					.unwrap()
					.then(() => {
						navigate(`/schools`);
					})
					.catch((error) => {
						setSaveError(error.data);
						scrollTo(0, 0);
					});
			}
		}
	}

	function handleOutsideSiteIdUpdate(id: string) {
		setOutsideSiteId(id);

		if (props.setSchoolInfo)
			props.setSchoolInfo({
				...props.schoolInfo,
				cleverID: props.schoolInfo.showCleverID ? id : props.schoolInfo.cleverID,
				classLinkID: props.schoolInfo.showClassLinkID ? id : props.schoolInfo.classLinkID,
			});
	}

	const handleOutsideSettingsFormClose = () => setIsOutsideSettingsFormOpen(false);

	function handleGenerateReferralCode() {
		generateReferralCode(props.schoolInfo.id)
			.unwrap()
			.then((code) => {
				setReferralCode(code);
				setSaveError("");
			})
			.catch((error) => {
				setSaveError(error.data);
			});
	}

	return (
		<section>
			{!config?.isUtahComposeSite && !props.addingNew && <hr className="my-6" />}
			<div className={`${props.addingNew ? "mt-2" : "mt-6"} flex flex-col gap-x-12 w-full`}>
				<div className="flex justify-between">
					<h2 className="mb-2">School Information</h2>
					<p className="text-xs leading-6 text-gray-600">
						<RequiredField className="!text-sm" /> - Denotes a required field
					</p>
				</div>
				<AlertMessage
					className="mb-2"
					type={AlertLevel.Error}
					message={saveError}
					showIcon={true}
					onClear={() => setSaveError("")}
				/>
				<div
					className={`${props.addingNew ? "gap-y-2" : "gap-y-3"} grid grid-cols-1 gap-x-6 sm:grid-cols-6`}>
					<Field className={`${props.addingNew ? "col-span-full" : "sm:col-span-3"}`}>
						<Label htmlFor="schoolName" className="font-medium text-sm">
							Name
							<RequiredField />
						</Label>
						<Input
							id="schoolName"
							name="schoolName"
							type="text"
							required
							maxLength={100}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={schoolName}
							onChange={(e) => setSchoolName(e.target.value)}
						/>
					</Field>
					<Field className="sm:col-span-3">
						<Select
							value={selectedDistrictId}
							setValue={(v) => setSelectedDistrictId(v as number)}
							label="District"
							requiredField={true}
							simpleListBoxId="districtDropdown"
							options={districtOptions}
							fieldClasses="md:pt-0"
							simpleListBoxClasses="w-full"
						/>
					</Field>
					{showOutsideSite && (
						<Field className="sm:col-span-2">
							<Label htmlFor="classlinkClever" className="font-medium text-sm">
								{props.schoolInfo.showCleverID ? "Clever" : "ClassLink"} ID
							</Label>
							<div className="flex mt-1">
								<Input
									id="classlinkClever"
									name="classlinkClever"
									type="text"
									readOnly
									className="rounded-none rounded-l-lg block w-full border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={outsideSiteId}
								/>
								<Button
									id="outside-site-form-button"
									className="rounded-lg rounded-l-none border-0 ring-1 ring-inset ring-gray-900/20 cursor-pointer flex justify-center overflow-hidden"
									onClick={() => setIsOutsideSettingsFormOpen(true)}>
									<ClassLinkOrCleverIcon
										iconClasses="h-9 w-10 !rounded-lg !rounded-l-none"
										isClassLinkEntity={props.schoolInfo.showClassLinkID}
										isCleverEntity={props.schoolInfo.showCleverID}
									/>
								</Button>
							</div>
							{isOutsideSettingsFormOpen && (
								<ModalFrame
									open={isOutsideSettingsFormOpen}
									title={`${props.schoolInfo.showCleverID ? "Clever Settings" : "ClassLink Settings"}`}
									onClose={handleOutsideSettingsFormClose}>
									<SchoolOutsideSiteSettingsForm
										schoolId={props.schoolInfo.id}
										isCleverForm={props.schoolInfo.showCleverID}
										onUpdateOutsideSiteId={handleOutsideSiteIdUpdate}
										onClose={handleOutsideSettingsFormClose}
									/>
								</ModalFrame>
							)}
						</Field>
					)}
					<Field className={`${showOutsideSite ? "sm:col-span-2" : "sm:col-span-3"}`}>
						<Label htmlFor="externalId" className="font-medium text-sm">
							External ID
						</Label>
						<Input
							id="externalId"
							name="externalId"
							type="text"
							maxLength={40}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={externalId}
							onChange={(e) => setExternalId(e.target.value)}
						/>
					</Field>
					{!props.addingNew && (
						<Field className={`${showOutsideSite ? "sm:col-span-2" : "sm:col-span-3"}`}>
							<Label htmlFor="referralCode" className="font-medium text-sm">
								Referral Code
							</Label>
							<div className="flex mt-1">
								<Input
									id="referralCode"
									name="referralCode"
									type="text"
									readOnly
									value={referralCode}
									className={`${role && isHigherThanDistrictAdmin(role) ? "rounded-none rounded-l-lg" : "rounded-lg"} block w-full border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600`}
								/>
								{role && isHigherThanDistrictAdmin(role) && (
									<>
										<ButtonWithConfirm
											tooltipId="referralCode-tooltip"
											ariaLabel="Generate New Referral Code"
											className="relative border border-gray-300 inline-flex items-center rounded-r-lg px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-50"
											message="Do you want to generate a new referral code for the school? This will overwrite any previous codes."
											confirmLabel="Yes"
											onConfirm={handleGenerateReferralCode}>
											<ArrowPathIcon className="size-5" />
										</ButtonWithConfirm>
										<Tooltip
											id="referralCode-tooltip"
											place="bottom-end"
											content="Generate New Referral Code"
											openEvents={{
												mouseenter: true,
												focus: false,
												click: false,
											}}
											closeEvents={{
												mouseleave: true,
												blur: true,
											}}
										/>
									</>
								)}
							</div>
						</Field>
					)}
				</div>
			</div>

			<div className="mt-2">
				<div
					className="flex items-center cursor-pointer"
					onClick={toggleShowAddressSection}>
					{showAddressSection ? (
						<ChevronDownIcon
							tabIndex={0}
							className="size-5 hover:cursor-pointer"
							onClick={toggleShowAddressSection}
							onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowAddressSection)}
						/>
					) : (
						<ChevronRightIcon
							tabIndex={0}
							className="size-5 hover:cursor-pointer"
							onClick={toggleShowAddressSection}
							onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowAddressSection)}
						/>
					)}
					<h3 className="text-base font-semibold text-gray-900 pt-2 pb-2 ml-4">
						Address and Contact Information
					</h3>
				</div>
				{showAddressSection && (
					<div className="flex gap-x-12 w-full">
						<div className="flex-col w-3/5">
							<h2 className="mb-2">Address</h2>
							<div
								className={`${props.addingNew ? "gap-y-2" : "gap-y-4"} grid grid-cols-1 gap-x-6 sm:grid-cols-9`}>
								<Field className="col-span-full">
									<Label htmlFor="line1" className="font-medium text-sm">
										Street Address
										<RequiredField />
									</Label>
									<Input
										id="line1"
										name="line1"
										type="text"
										required
										maxLength={150}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={streetAddress}
										onChange={(e) => setStreetAddress(e.target.value)}
									/>
								</Field>
								<Field className="col-span-full">
									<Label htmlFor="line2" className="font-medium text-sm">
										Street Address Line 2
									</Label>
									<Input
										id="line2"
										name="line2"
										type="text"
										maxLength={150}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={streetAddress2}
										onChange={(e) => setStreetAddress2(e.target.value)}
									/>
								</Field>
								<Field
									className={`${props.addingNew ? "col-span-4" : "col-span-full md:col-span-4 2xl:col-span-2"}`}>
									<Label htmlFor="city" className="font-medium text-sm">
										City
										<RequiredField />
									</Label>
									<Input
										id="city"
										name="city"
										type="text"
										required
										maxLength={35}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={city}
										onChange={(e) => setCity(e.target.value)}
									/>
								</Field>

								<Field
									className={`${props.addingNew ? "col-span-5" : "col-span-full md:col-span-5 2xl:col-span-3"}`}>
									<Select
										value={selectedStateId}
										setValue={(v) => setSelectedStateId(v as number)}
										label="State"
										requiredField={true}
										simpleListBoxId="stateDropdown"
										options={stateOptions}
										simpleListBoxClasses="w-full"
									/>
								</Field>
								<Field
									className={`${props.addingNew ? "col-span-6" : "col-span-full md:col-span-6 2xl:col-span-3"}`}>
									<Select
										value={selectedCountryId}
										setValue={(v) => setSelectedCountryId(v as number)}
										label="Country"
										requiredField={true}
										simpleListBoxId="countryDropdown"
										options={countryOptions}
										simpleListBoxClasses="w-full"
									/>
								</Field>

								<Field
									className={`${props.addingNew ? "col-span-3" : "col-span-full md:col-span-3 2xl:col-span-1"}`}>
									<Label htmlFor="zip" className="font-medium text-sm">
										ZIP Code
									</Label>
									<Input
										id="zip"
										name="zip"
										type="text"
										maxLength={10}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={zip}
										onChange={(e) => setZip(e.target.value)}
									/>
								</Field>
							</div>
						</div>
						<div className="flex-col w-2/5">
							<h2 className="mb-2">Contact Information</h2>
							<div
								className={`${props.addingNew ? "gap-y-2" : "gap-y-4"} grid grid-cols-1 gap-x-6 sm:grid-cols-6`}>
								<Field
									className={`${props.addingNew ? "col-span-full" : "col-span-5"}`}>
									<Label htmlFor="contactName" className="font-medium text-sm">
										Name
										<RequiredField />
									</Label>
									<Input
										id="contactName"
										name="contactName"
										type="text"
										required
										maxLength={60}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={contactName}
										onChange={(e) => setContactName(e.target.value)}
									/>
								</Field>

								<Field
									className={`${props.addingNew ? "col-span-full" : "col-span-5"}`}>
									<Label htmlFor="email" className="font-medium text-sm">
										Email
									</Label>
									<Input
										id="email"
										name="email"
										type="text"
										maxLength={256}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={contactEmail}
										onChange={(e) => setContactEmail(e.target.value)}
									/>
								</Field>

								<Field
									className={`${props.addingNew ? "col-span-full" : "col-span-4 lg:col-span-3"}`}>
									<Label htmlFor="phone" className="font-medium text-sm">
										Phone
										<RequiredField />
									</Label>
									<Input
										id="phone"
										name="phone"
										type="text"
										required
										maxLength={255}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={contactPhone}
										onChange={(e) => setContactPhone(e.target.value)}
									/>
								</Field>
							</div>
						</div>
					</div>
				)}
			</div>
			<div className="flex items-center justify-end gap-x-4">
				<Button type="button" className="btn btn-naked" onClick={handleCancel}>
					Cancel
				</Button>
				<Button
					type="submit"
					className="btn btn-primary"
					disabled={disableSave}
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSave(e)}>
					Save
				</Button>
			</div>
		</section>
	);
}
